import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { Reveal, ChapterTag, Marquee } from "../components/Motion";
import Seo from "../components/Seo";
import { PageHero, Loading } from "../components/Sections";

export default function Services() {
  const { data, loading } = useApi(api.services, []);

  return (
    <PageShell>
      {loading || !data ? (
        <Loading />
      ) : (
        <>
          <Seo
            title="SIA Security Guard Services UK | Patrols & Events | Xtreme Protection"
            description="Compare manned guarding, mobile patrols, event security and CCTV support across the UK. Discuss site needs and obtain a tailored security proposal."
            keywords="security guard services UK, manned guarding London, mobile security patrols Manchester, event security Birmingham, SIA security company UK"
            path="/services"
          />
          <PageHero
            eyebrow="THE ARSENAL // SPECIALIST SERVICES"
            lines={["SECURITY GUARD", "SERVICES ACROSS", "THE UK"]}
            intro={[
              "From fixed posts to mobile patrols, choose coverage around your site, operating hours and risks. Explore each service and request a scoped plan.",
              "Every Xtreme Protection service is delivered by SIA-licensed, BS 7858 vetted professionals with verified digital reporting — one contract, one accountable partner, one standard of proof.",
            ]}
            breadcrumb={[{ label: "Home", path: "/" }, { label: "Services", path: "/services" }]}
          />
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="services-index">
            {data.services.map((s, i) => (
              <Reveal key={s.slug} delay={0}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-row-${s.slug}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-white/10 py-6 sm:py-8 hover:bg-coal/50 transition-colors duration-300 px-2 sm:px-4"
                >
                  <span className="col-span-2 sm:col-span-1 font-mono text-xs text-amber tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-10 sm:col-span-5 font-display font-bold uppercase tracking-wide text-xl sm:text-3xl text-white group-hover:text-amber transition-colors leading-tight">
                    {s.name}
                  </span>
                  <span className="col-span-10 col-start-3 sm:col-span-5 sm:col-start-auto text-xs sm:text-sm text-gray-400 leading-relaxed">
                    {s.short}
                  </span>
                  <span className="hidden sm:flex col-span-1 justify-end">
                    <ArrowUpRight className="w-5 h-5 text-gray-500 group-hover:text-amber group-hover:rotate-45 transition-all duration-300" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Marquee items={data.services.flatMap((s) => [s.name, s.keywords[0].replace(/\b\w/g, (c) => c.toUpperCase())])} />
          <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
            <ChapterTag num="UK" title="NATIONWIDE DELIVERY" />
            <p className="max-w-3xl text-sm sm:text-base text-gray-400 leading-relaxed">
              Every service above is available in more than 210 UK towns and cities — from London, Manchester and
              Birmingham to Glasgow, Cardiff and Belfast.{" "}
              <Link to="/locations" className="text-amber underline underline-offset-4 hover:text-white transition-colors">
                Browse the full UK locations directory
              </Link>{" "}
              to find detailed service information for your town or city.
            </p>
          </section>
        </>
      )}
    </PageShell>
  );
}
